
import { logStorage, isLocalStorageAvailable } from './storageUtils';

// Generic in-memory storage system that can be used for different types of data
export class StorageEngine<T extends { id: number }> {
  private storageKey: string;
  private inMemoryStorage: T[] = [];
  private idCounter: number;
  private logPrefix: string;

  constructor(storageKey: string, logPrefix: string) {
    this.storageKey = storageKey;
    this.logPrefix = logPrefix;
    this.idCounter = Date.now();
    this.initializeStorage();
  }

  // Initialize storage from localStorage if available
  private initializeStorage(): void {
    logStorage(this.logPrefix, `Initializing ${this.storageKey} storage system`);
    
    if (isLocalStorageAvailable()) {
      try {
        const savedItems = localStorage.getItem(this.storageKey);
        if (savedItems) {
          const items = JSON.parse(savedItems);
          logStorage(this.logPrefix, `Loaded ${items.length} items from localStorage`);
          this.inMemoryStorage = [...items];
          
          // Update ID counter to be higher than any existing ID
          const maxId = Math.max(0, ...items.map((item: T) => item.id));
          this.idCounter = Math.max(this.idCounter, maxId + 1);
          logStorage(this.logPrefix, `Updated ID counter to ${this.idCounter} based on loaded items`);
        } else {
          logStorage(this.logPrefix, "No saved items found in localStorage");
        }
      } catch (e) {
        console.error(`Error loading initial ${this.storageKey} from localStorage:`, e);
      }
    } else {
      logStorage(this.logPrefix, "localStorage not available, using in-memory storage only");
    }
  }

  // Get all items from storage
  getAllItems(): T[] {
    logStorage(this.logPrefix, "Getting all items from storage");
    return [...this.inMemoryStorage];
  }

  // Save a single item to storage
  saveItem(item: T): T {
    // Ensure the item has an ID
    if (!item.id) {
      item = { ...item, id: this.idCounter++ };
    }
    
    logStorage(this.logPrefix, `Saving item to storage (ID: ${item.id})`);
    
    // Add to in-memory storage first
    const updatedItems = [...this.inMemoryStorage, item];
    this.inMemoryStorage = updatedItems;
    
    // Then try localStorage if available
    if (isLocalStorageAvailable()) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(updatedItems));
        logStorage(this.logPrefix, "Item saved to localStorage successfully");
      } catch (e) {
        console.error('Failed to save to localStorage:', e);
        logStorage(this.logPrefix, "Item saved to in-memory only due to localStorage error");
      }
    } else {
      logStorage(this.logPrefix, "localStorage not available, saving to in-memory only");
    }
    
    return item;
  }

  // Save multiple items to storage - completely replace existing items
  saveItems(items: T[]): T[] {
    logStorage(this.logPrefix, `Saving ${items.length} items to storage`);
    
    // Update in-memory storage first for reliability
    this.inMemoryStorage = [...items];
    
    // Then try localStorage if available
    if (isLocalStorageAvailable()) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(items));
        logStorage(this.logPrefix, "Items saved to localStorage successfully");
      } catch (e) {
        console.error('Failed to save to localStorage:', e);
        logStorage(this.logPrefix, "Items saved to in-memory only due to localStorage error");
      }
    } else {
      logStorage(this.logPrefix, "localStorage not available, saving to in-memory only");
    }
    
    return items;
  }
  
  // Add multiple items to existing storage without replacing all items
  addItems(newItems: T[]): T[] {
    logStorage(this.logPrefix, `Adding ${newItems.length} items to storage`);
    
    // Ensure all items have IDs
    const itemsWithIds = newItems.map(item => {
      if (!item.id) {
        return { ...item, id: this.idCounter++ };
      }
      return item;
    });
    
    // Add to in-memory storage
    const updatedItems = [...this.inMemoryStorage, ...itemsWithIds];
    this.inMemoryStorage = updatedItems;
    
    // Then try localStorage if available
    if (isLocalStorageAvailable()) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(updatedItems));
        logStorage(this.logPrefix, "Items added to localStorage successfully");
      } catch (e) {
        console.error('Failed to save to localStorage:', e);
        logStorage(this.logPrefix, "Items added to in-memory only due to localStorage error");
      }
    } else {
      logStorage(this.logPrefix, "localStorage not available, adding to in-memory only");
    }
    
    return itemsWithIds;
  }

  // Delete an item from storage
  deleteItem(itemId: number): boolean {
    logStorage(this.logPrefix, "Deleting item with ID:", itemId);
    
    // Update in-memory storage
    const updatedItems = this.inMemoryStorage.filter(item => item.id !== itemId);
    this.inMemoryStorage = updatedItems;
    
    if (isLocalStorageAvailable()) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(updatedItems));
        logStorage(this.logPrefix, "Item deleted from localStorage successfully");
      } catch (e) {
        console.error('Failed to update localStorage after deletion:', e);
        logStorage(this.logPrefix, "Item deleted from in-memory only");
      }
    }
    
    return true;
  }

  // Update an item in storage
  updateItem(itemId: number, updatedFields: Partial<T>): T | null {
    logStorage(this.logPrefix, "Updating item with ID:", itemId);
    
    // Find item in in-memory storage
    const itemIndex = this.inMemoryStorage.findIndex(item => item.id === itemId);
    
    if (itemIndex === -1) {
      logStorage(this.logPrefix, "Item not found for update");
      return null;
    }
    
    // Update in-memory storage
    const updatedItems = [...this.inMemoryStorage];
    updatedItems[itemIndex] = { ...updatedItems[itemIndex], ...updatedFields };
    this.inMemoryStorage = updatedItems;
    
    if (isLocalStorageAvailable()) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(updatedItems));
        logStorage(this.logPrefix, "Item updated in localStorage successfully");
      } catch (e) {
        console.error('Failed to update localStorage:', e);
        logStorage(this.logPrefix, "Item updated in in-memory only");
      }
    }
    
    return updatedItems[itemIndex];
  }
  
  // Filter items by a predicate function
  filterItems(predicate: (item: T) => boolean): T[] {
    return this.inMemoryStorage.filter(predicate);
  }
  
  // Debug helper to dump all items to console
  debugDumpStorage(): void {
    console.log(`===== ${this.storageKey.toUpperCase()} STORAGE DUMP =====`);
    console.log("In-memory storage:", this.inMemoryStorage);
    
    if (isLocalStorageAvailable()) {
      try {
        const savedItems = localStorage.getItem(this.storageKey);
        console.log("localStorage:", savedItems ? JSON.parse(savedItems) : null);
      } catch (e) {
        console.error("Error reading localStorage:", e);
      }
    } else {
      console.log("localStorage: NOT AVAILABLE");
    }
    console.log("============================");
  }
}
